/**
 * Pillar F.2 — the participant hovercard: hover a LINKED participant, get a small tile with their
 * name, title and firm.
 *
 * Three deliberate properties:
 *
 *  - **Nothing is fetched until the pointer settles.** The tile is not rendered — and therefore
 *    `useCrmPersonCard` is not even called — until the hover has survived `HOVER_DELAY_MS`. Sweeping
 *    a pointer across a twelve-person recipient list issues zero requests. The endpoint is audited,
 *    so a hover that fires a read per mouse-move would also be writing audit rows per mouse-move.
 *  - **Only linkable people get one.** This component is mounted by the participant renderer solely
 *    around names it already decided to LINK — external and resolved. An internal colleague's name
 *    renders as plain text with no wrapper, so no hovercard and no request (their profile is 404 by
 *    design, and probing it per hover would be exactly the existence oracle the gate prevents).
 *  - **It never takes focus, but it IS announced.** Unlike `CrmPopover` (a menu, which must own the
 *    keyboard), this is a passive tile: portaled out to escape overflow clipping, positioned from the
 *    anchor's rect, and `pointer-events: none` so it can never swallow the click on the link it
 *    describes. Keyboard users get the same tile on focus of the link, and the tile is wired to that
 *    link via `aria-describedby` → `role="tooltip"` so a screen reader actually reads it.
 *    ⚠ It used to carry `aria-hidden="true"`, which made this docstring's keyboard-parity claim false:
 *    focus opened a tile assistive tech had been explicitly told to ignore (review minor 8).
 */
import {
  cloneElement, isValidElement, useEffect, useId, useRef, useState,
  type CSSProperties, type ReactElement, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { useCrmPersonCard } from '../../hooks/useCrm';
import styles from './crm.module.css';

/** The debounce the ruling asks for (~200ms) — long enough that a sweep costs nothing. */
export const HOVER_DELAY_MS = 200;

function HovercardTile({ personId, anchorEl, tileId }: {
  personId: number; anchorEl: HTMLElement; tileId: string;
}) {
  // Mounted only once the debounce elapsed → this IS the lazy load. React Query's cache makes the
  // second hover over the same person free.
  const card = useCrmPersonCard(personId, true);
  const rect = anchorEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const style: CSSProperties = {
    position: 'fixed',
    zIndex: 1300,
    left: Math.round(Math.max(8, Math.min(rect.left, window.innerWidth - 280))),
    ...(spaceBelow < 120 && rect.top > spaceBelow
      ? { bottom: Math.round(window.innerHeight - rect.top + 6) }
      : { top: Math.round(rect.bottom + 6) }),
  };
  return createPortal(
    <div className={styles.hoverCard} style={style} id={tileId} role="tooltip">
      {card.isLoading && <div className={styles.hoverCardMuted}>Loading…</div>}
      {/* A 404 here is the ordinary governed answer (gated / merged away), not an error worth a
          scary message — the link itself still works or does not, on the server's terms. */}
      {card.error != null && <div className={styles.hoverCardMuted}>No details available.</div>}
      {card.data && (
        <>
          <div className={styles.hoverCardName}>{card.data.name || '(unnamed)'}</div>
          {card.data.title && <div className={styles.hoverCardLine}>{card.data.title}</div>}
          {card.data.firm && <div className={styles.hoverCardFirm}>{card.data.firm}</div>}
          {!card.data.title && !card.data.firm && (
            <div className={styles.hoverCardMuted}>No title or firm on record.</div>
          )}
        </>
      )}
    </div>,
    document.body,
  );
}

interface Props {
  personId: number;
  children: ReactNode;
}

export function CrmPersonHovercard({ personId, children }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const arm = (el: HTMLElement) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setAnchor(el), HOVER_DELAY_MS);
  };
  const disarm = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setAnchor(null);
  };
  // A pending timer must not fire after unmount (the timeline row collapses, the modal closes) —
  // it would set state on a gone component and, worse, issue the fetch it was cancelled to avoid.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Point the TRIGGER at the tile so assistive tech associates the two. It has to go on the
  // interactive element, not this wrapper span, or nothing announces it — hence cloneElement over the
  // single anchor/button child. A non-element child (bare text) simply gets no association, which is
  // correct: there is nothing focusable to describe.
  const tileId = `crm-hovercard-${useId()}`;
  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>,
                   { 'aria-describedby': anchor ? tileId : undefined })
    : children;

  return (
    <span
      className={styles.hoverWrap}
      onMouseEnter={(e) => arm(e.currentTarget)}
      onMouseLeave={disarm}
      onFocus={(e) => arm(e.currentTarget)}
      onBlur={disarm}
    >
      {trigger}
      {anchor && <HovercardTile personId={personId} anchorEl={anchor} tileId={tileId} />}
    </span>
  );
}
